import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { business } from "../data/content";

const links = [
  { to: "/", label: "Home" },
  { to: "/booking", label: "Booking" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-cream/95 backdrop-blur border-b border-ink/10"
          : "bg-transparent"
      }`}
    >
      <nav className="container-page flex items-center justify-between py-5">
        <NavLink
          to="/"
          onClick={() => setOpen(false)}
          className="flex items-center"
        >
          <img
            src={business.logo}
            alt={business.name}
            className={`h-11 w-auto transition-all duration-300 ${
              scrolled ? "" : "brightness-0 invert drop-shadow-sm"
            }`}
          />
          <span className="sr-only">{business.name}</span>
        </NavLink>
        <div className="hidden md:flex items-center gap-10">
          <ul className="flex items-center gap-9 font-body text-[0.95rem]">
            {links.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end
                  className={`relative pb-1 transition-colors ${
                    scrolled
                      ? "text-ink/70 hover:text-ink"
                      : "text-cream/85 hover:text-cream"
                  }`}
                >
                  {({ isActive }) => (
                    <>
                      {link.label}
                      {isActive && (
                        <motion.span
                          layoutId="nav-underline"
                          className={`absolute -bottom-0.5 left-0 right-0 h-[1.5px] ${
                            scrolled ? "bg-lightgreen" : "bg-cream"
                          }`}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          <span
            className={`h-5 w-px transition-colors ${
              scrolled ? "bg-ink/15" : "bg-cream/25"
            }`}
            aria-hidden="true"
          />

          <NavLink
            to="/booking"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 ${
              scrolled
                ? "bg-darkgreen text-cream shadow-sm shadow-darkgreen/30 hover:shadow-md hover:shadow-darkgreen/40"
                : "bg-cream text-darkgreen shadow-sm shadow-black/10 hover:shadow-md hover:shadow-black/15"
            }`}
          >
            Book Now
            <span aria-hidden="true">→</span>
          </NavLink>
        </div>
        <button
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden relative h-8 w-8 shrink-0"
        >
          <span
            className={`absolute left-0 block h-[1.5px] w-8 transition-transform duration-300 ${
              scrolled ? "bg-ink" : "bg-cream"
            } ${open ? "top-1/2 rotate-45" : "top-[10px]"}`}
          />
          <span
            className={`absolute left-0 top-1/2 block h-[1.5px] w-8 transition-opacity duration-200 ${
              scrolled ? "bg-ink" : "bg-cream"
            } ${open ? "opacity-0" : "opacity-100"}`}
          />
          <span
            className={`absolute left-0 block h-[1.5px] w-8 transition-transform duration-300 ${
              scrolled ? "bg-ink" : "bg-cream"
            } ${open ? "top-1/2 -rotate-45" : "top-[22px]"}`}
          />
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-cream border-b border-ink/10"
          >
            <ul className="container-page flex flex-col gap-1 py-4">
              {links.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block py-3 font-display text-2xl ${isActive ? "text-moss" : "text-ink"}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-2">
                <NavLink
                  to="/booking"
                  onClick={() => setOpen(false)}
                  className="inline-flex rounded-full bg-moss px-5 py-2.5 text-sm text-cream"
                >
                  Book Now
                </NavLink>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
